<template>
  <div
    v-if="isModalOpen"
    class="tw:fixed tw:inset-0 tw:top-0 tw:z-40 tw:w-full tw:h-full tw:overflow-hidden tw:flex tw:overflow-y-auto"
    :class="overlay === 'light' ? 'overlay-white-75' : 'overlay-black-60'"
  >
    <div class="modal-dialog" :data-testid="dataTestId">
      <div
        class="tw:bg-white tw:md:rounded-xl base-card tw:overflow-hidden modal-content"
        :class="props.customClasses"
        v-click-outside="onClickAwayFn"
      >
        <div v-if="withHeader" class="modal-header tw:p-6 tw:pb-2 tw:pt-6 tw:border-b tw:border-gray-300" @click.stop>
          <div class="tw:flex tw:w-full tw:pb-4 tw:justify-between">
            <slot name="modalHeader" />
            <div class="tw:justify-end tw:self-center tw:flex">
              <button
                class="tw:outline-hidden tw:absolute tw:top-[30px] tw:right-[30px] tw:cursor-pointer"
                data-cy="close-modal-button"
                @click="toggleModal"
                @keydown.enter="toggleModal"
              >
                <Close class="tw:size-3.5" />
              </button>
            </div>
          </div>
        </div>

        <div
          v-else-if="withCloseBtn"
          class="modal-header tw:relative"
          :class="{ 'tw:p-5 tw:pb-0 tw:pt-4': !noPadding }"
        >
          <button
            class="tw:outline-hidden tw:absolute tw:right-[30px] tw:top-[30px] tw:z-10 tw:cursor-pointer"
            @click="toggleModal()"
            @keydown.enter="toggleModal()"
          >
            <Close class="tw:size-3.5" />
          </button>
        </div>
        <div class="tw:relative tw:flex tw:grow tw:overflow-hidden">
          <div
            ref="modalBody"
            class="modal-body tw:flex tw:grow tw:overflow-y-auto"
            :class="{ 'tw:px-6 tw:py-6': !noPadding }"
            @click.stop
            @scroll="handleScroll"
          >
            <slot />
          </div>

          <!-- Bottom shadow -->
          <div
            v-if="showBottomShadow"
            class="tw:absolute tw:bottom-0 tw:left-0 tw:right-0 tw:h-20 tw:pointer-events-none"
            style="background: linear-gradient(to top, rgba(255, 255, 255, 1) 0%, rgba(255, 255, 255, 0) 100%)"
          />
        </div>
        <div
          v-if="withFooter"
          @click.stop
          class="modal-footer tw:flex tw:md:px-6 tw:md:py-4 tw:md:pt-4 tw:px-6 tw:py-4 tw:items-center tw:space-x-2 tw:rounded-b tw:border-t tw:border-gray-300"
        >
          <slot name="modalFooter" />
        </div>
        <div v-show="showLoading" class="overlay-white-75 tw:absolute tw:h-full tw:w-full">
          <LaunchpadLoader class="tw:h-full tw:flex tw:justify-center tw:items-center" />
        </div>
      </div>
    </div>
  </div>
</template>
<script setup>
import { watch, onMounted, onBeforeUnmount, ref, nextTick } from 'vue'
import Close from '@/components/icons/Close.svg?component'
import LaunchpadLoader from '@/components/dashboard/common/LaunchpadLoader.vue'

const props = defineProps({
  isModalOpen: {
    type: Boolean,
    required: true,
    default: false,
  },
  customClasses: {
    type: String,
    default: '',
  },
  overlay: {
    type: String,
    default: 'light',
  },
  withFooter: {
    type: Boolean,
    default: false,
  },
  withHeader: {
    type: Boolean,
    default: true,
  },
  showLoading: {
    type: Boolean,
    default: false,
  },
  withCloseBtn: {
    type: Boolean,
    default: true,
  },
  noPadding: {
    type: Boolean,
    default: false,
  },
  dataTestId: {
    type: String,
    default: '',
  },
})

const emit = defineEmits(['toggleModal', 'onClickAway'])
const toggleModal = () => emit('toggleModal')

const modalBody = ref(null)
const showBottomShadow = ref(false)

const onClickAwayFn = () => emit('onClickAway')
const handleEscape = e => {
  if (e.key === 'Escape' && props.isModalOpen) {
    toggleModal()
  }
}

const handleScroll = () => {
  if (!modalBody.value) return

  const element = modalBody.value
  const scrollTop = element.scrollTop
  const scrollHeight = element.scrollHeight
  const clientHeight = element.clientHeight

  // Show shadow if not scrolled to bottom (with 5px threshold)
  showBottomShadow.value = scrollTop + clientHeight < scrollHeight - 5
}

const checkScroll = () => {
  nextTick(() => {
    handleScroll()
  })
}

watch(
  () => props.isModalOpen,
  () => {
    handleBodyScroll(props.isModalOpen)
    if (props.isModalOpen) {
      checkScroll()
    } else {
      showBottomShadow.value = false
    }
  }
)

onMounted(() => {
  if (props.isModalOpen) {
    handleBodyScroll(props.isModalOpen)
    checkScroll()
  }
  document.addEventListener('keydown', handleEscape)
  window.addEventListener('resize', checkScroll)
})

onBeforeUnmount(() => {
  handleBodyScroll(false)
  document.removeEventListener('keydown', handleEscape)
  window.removeEventListener('resize', checkScroll)
})

const handleBodyScroll = value => {
  if (value) {
    document.body.classList.add('tw:overflow-hidden')
  } else {
    document.body.classList.remove('tw:overflow-hidden')
  }
}
</script>
<style scoped>
.modal-dialog {
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  max-height: 100%;
  min-height: 580px;

  @media (min-width: 768px) {
    max-height: calc(100% - 100px);
    margin: auto;
  }
}
.modal-content {
  position: relative;
  display: flex;
  flex-direction: column;
  pointer-events: auto;
  background-color: #fff;
  background-clip: padding-box;
  border: 0 solid rgba(0, 0, 0, 0.2);
  outline: 0;
}

.base-card {
  box-shadow:
    0 2px 4px 0 rgba(63, 87, 189, 0.1),
    0 2px 10px 0 rgba(108, 112, 122, 0.15);
}
</style>
